import { useState, useEffect, useRef, useCallback } from 'react';
import { IonSpinner, IonIcon, createAnimation } from '@ionic/react';
import { people, school, construct, documentText, cart, leaf, cog, brush, apps } from 'ionicons/icons';
import { searchOfoCodes, recordContextView, isAiReady, whenAiReady, isModelReady, whenModelReady, type OfoSuggestion, type ContextTerm } from '@/lib/ofo-client-search';

/**
 * Major categories. In the OFO-2021 dataset shipped with BluePages the major
 * group is identified by the first digit of the occupation `code` (1–8; there
 * is no group 9). We key the icon + colour off `code[0]`.
 */
interface MajorGroupMeta {
  icon: string;
  color: string;
}
const MAJOR_GROUPS: Record<string, MajorGroupMeta> = {
  '1': { icon: people, color: '#60a5fa' }, // Managers
  '2': { icon: school, color: '#34d399' }, // Professionals
  '3': { icon: construct, color: '#22d3ee' }, // Technicians & Associate Professionals
  '4': { icon: documentText, color: '#a78bfa' }, // Clerical Support Workers
  '5': { icon: cart, color: '#fbbf24' }, // Service & Sales Workers
  '6': { icon: leaf, color: '#4ade80' }, // Skilled Agricultural, Forestry, Fishery & Craft
  '7': { icon: cog, color: '#f472b6' }, // Plant & Machine Operators & Assemblers
  '8': { icon: brush, color: '#94a3b8' }, // Elementary Occupations
};
const DEFAULT_GROUP: MajorGroupMeta = { icon: apps, color: '#94a3b8' };

function majorGroupMeta(ofo: OfoSuggestion): MajorGroupMeta {
  return MAJOR_GROUPS[ofo.code.charAt(0)] ?? DEFAULT_GROUP;
}

interface OfoAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (ofo: OfoSuggestion) => void;
  placeholder?: string;
  onSearch?: () => void;
  /** Search context keywords that bias ranking toward the user's prior context. */
  context?: ContextTerm[];
  /** Notifies the parent whenever the presence of results changes. */
  onResultsChange?: (hasResults: boolean) => void;
}

function skillLevelColor(level: string): string {
  switch (level) {
    case 'High': return '#10b981';
    case 'Medium': return '#f59e0b';
    default: return '#6b7280';
  }
}

/** Each word capitalised — e.g. "Automotive Motor Mechanic". */
function titleCase(text: string): string {
  if (!text) return text;
  return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export function OfoAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  onSearch,
  context,
  onResultsChange,
}: OfoAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<OfoSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<number | null>(null);

  // Stable signature of the current result set. When it changes we remount the
  // results body (re-firing the staggered row-in animation) and FLIP-animate
  // the container height so updates glide instead of snapping.
  const resultsKey = suggestions.map((s) => s.code).join('|');

  // Record the chosen occupation as a (recently-viewed) search-context term.
  // `recordContextView` timestamps it so `buildSearchContext` can apply
  // exponential-decay weighting — a prior "Electrician" selection floats
  // "Electrician" above "Electrical Engineer" on later partial queries.
  const handleSelect = useCallback(
    (ofo: OfoSuggestion) => {
      recordContextView(ofo.description);
      onSelect(ofo);
    },
    [onSelect],
  );

  const runSearch = useCallback(
    async (q: string, ctx?: ContextTerm[]) => {
      if (q.trim().length < 2) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
    setLoading(true);
    try {
      const results = await searchOfoCodes(q, 6, ctx);
      setSuggestions(results);
      setOpen(true);
      setActiveIndex(-1);
      if (!isAiReady()) whenAiReady(() => runSearch(q, ctx));
      if (!isModelReady()) whenModelReady(() => runSearch(q, ctx));
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value, context), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, context, runSearch]);

  // Tell the parent whenever the presence of results changes, so it can
  // crossfade the surrounding UI (e.g. the "Try" suggestion chips).
  useEffect(() => {
    onResultsChange?.(suggestions.length > 0);
  }, [suggestions.length, onResultsChange]);

  // Reset the tracked height whenever the results collapse, so the next open
  // fades in fresh instead of animating from a stale height.
  useEffect(() => {
    if (!open || suggestions.length === 0) prevHeightRef.current = null;
  }, [open, suggestions.length]);

  // Smoothly transition to updated results: crossfade the freshly-mounted body
  // and FLIP the container height from its previous value to the new one, so a
  // change in the result set glides instead of snapping. Keyed on `resultsKey`
  // so it runs once per distinct result set.
  useEffect(() => {
    const container = resultsRef.current;
    const body = bodyRef.current;
    if (!container || !body) return;

    const nextHeight = body.offsetHeight;
    const prevHeight = prevHeightRef.current;
    prevHeightRef.current = nextHeight;

    const fade = createAnimation()
      .addElement(body)
      .duration(220)
      .easing('cubic-bezier(0.4, 0, 0.2, 1)')
      .fromTo('opacity', '0', '1')
      .fromTo('transform', 'translateY(4px)', 'translateY(0)');

    if (prevHeight !== null && prevHeight !== nextHeight) {
      const height = createAnimation()
        .addElement(container)
        .duration(260)
        .easing('cubic-bezier(0.4, 0, 0.2, 1)')
        .fromTo('maxHeight', `${prevHeight}px`, `${nextHeight}px`);
      createAnimation().addAnimation([height, fade]).play();
    } else {
      fade.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'Enter') onSearch?.();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0) {
        setOpen(false);
        handleSelect(suggestions[activeIndex]);
      } else {
        onSearch?.();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className="bp-search-bar bp-search-bar-lg">
        <svg
          width="20" height="20" viewBox="0 0 24 24" fill="none"
          style={{ color: 'var(--bp-blue-400)', margin: 'auto 0 auto 12px', flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--ion-text-color)',
            fontSize: '0.9375rem',
            padding: '12px 8px',
          }}
        />
        {loading && (
          <IonSpinner name="crescent" style={{ margin: 'auto 12px auto 0', '--color': 'var(--bp-blue-400)' } as any} />
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div ref={resultsRef} className="bp-ofo-results bp-ofo-results-inline">
          <div key={resultsKey} ref={bodyRef}>
            <div className="bp-ofo-results-header">
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Recognised Occupations
                {isAiReady() && <span className="bp-ofo-ai-badge">AI</span>}
              </span>
            </div>
            {suggestions.map((ofo, idx) => {
              const meta = majorGroupMeta(ofo);
              return (
                <div
                  key={ofo.code}
                  className="bp-ofo-result-row"
                  onClick={() => { setOpen(false); handleSelect(ofo); }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  style={{
                    borderBottom: idx < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    background: idx === activeIndex ? 'rgba(59,130,246,0.12)' : 'transparent',
                    animationDelay: `${Math.min(idx, 6) * 0.035}s`,
                  }}
                >
                  <span
                    aria-label={ofo.majorGroup}
                    title={ofo.majorGroup}
                    className="bp-ofo-result-icon"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    <IonIcon icon={meta.icon} style={{ fontSize: '1.1rem' }} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--ion-text-color)' }}>
                      {titleCase(ofo.description)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted)' }}>
                        {[titleCase(ofo.majorGroup), titleCase(ofo.minorGroup)]
                          .filter(Boolean)
                          .join('  ·  ')}
                      </span>
                      <span style={{
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '4px',
                        background: `${skillLevelColor(ofo.skillLevel)}22`,
                        color: skillLevelColor(ofo.skillLevel),
                        textTransform: 'uppercase',
                      }}>
                        {ofo.skillLevel}
                      </span>
                    </div>
                    {ofo.tasks.length > 0 && (
                      <div style={{
                        fontSize: '0.6875rem',
                        color: 'var(--ion-color-medium)',
                        marginTop: '5px',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {ofo.tasks.slice(0, 2).map((t, i) => (
                          <span key={i}>
                            {i > 0 && ' · '}
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {ofo.generatedDescription && (
                      <div style={{
                        fontSize: '0.6875rem',
                        color: 'var(--bp-text-muted)',
                        marginTop: '5px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontStyle: 'italic',
                      }}>
                        {ofo.generatedDescription}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
